import { lazyMdaHoverHandlers } from '../LazyMdaDemo';
import { prefetchArticleBySlug } from '../../lib/publicContentCache';
import {
  articleHasHeroMedia,
  ArticleHeroMedia,
} from './ArticleHeroMedia';
import {
  resolveArticleHeroMedia,
  type ArticleHeroMedia as ArticleHeroMediaValue,
} from '../../../../shared/domain/articleHeroMedia';

const CARD_PADDING = {
  featured: 'px-6 pt-10',
  compact: 'px-6 py-10',
} as const;

const DEMO_SLOT_CLASS =
  'article-card-hero-slot mt-auto w-full overflow-hidden rounded-t-card';

interface ArticleCardProps {
  title: string;
  excerpt: string;
  slug: string;
  href?: string;
  date?: string;
  author?: string;
  heroMedia?: ArticleHeroMediaValue | null;
  miniDemoId?: string | null;
  variant?: keyof typeof CARD_PADDING;
}

function formatCardMeta(date?: string, author?: string): string | null {
  if (date && author) return `${date} by ${author}`;
  if (date) return date;
  if (author) return author;
  return null;
}

function resolveCardDemoId(
  heroMedia: ArticleHeroMediaValue | null | undefined,
  miniDemoId?: string | null,
): string | null | undefined {
  const resolved = resolveArticleHeroMedia({
    hero_media: heroMedia,
    mini_demo_id: miniDemoId,
  });
  if (resolved?.kind === 'demo' && resolved.demoId) {
    return resolved.demoId;
  }
  return miniDemoId;
}

export function ArticleCard({
  title,
  excerpt,
  slug,
  href,
  date,
  author,
  heroMedia,
  miniDemoId,
  variant = 'compact',
}: ArticleCardProps) {
  const isFeatured = variant === 'featured';
  const linkHref = href ?? `/articles/${slug}`;
  const meta = formatCardMeta(date, author);
  const hasHeroMedia = articleHasHeroMedia({
    hero_media: heroMedia,
    mini_demo_id: miniDemoId,
  });
  const cardDemoId = resolveCardDemoId(heroMedia, miniDemoId);

  return (
    <a
      href={linkHref}
      className={`group flex flex-col gap-4 rounded-card bg-surface-panel ${CARD_PADDING[variant]}`}
      onMouseEnter={() => prefetchArticleBySlug(slug)}
      onFocus={() => prefetchArticleBySlug(slug)}
      {...lazyMdaHoverHandlers(cardDemoId)}
    >
      <div className="flex flex-col gap-2">
        <p className="text-caption-lg text-text-primary">{title}</p>
        {meta && <p className="text-btn text-text-tertiary">{meta}</p>}
      </div>

      <p className="line-clamp-3 text-caption-lg text-text-tertiary">{excerpt}</p>

      {isFeatured && hasHeroMedia && (
        <div className={`${DEMO_SLOT_CLASS} bg-surface-frost-05`}>
          <ArticleHeroMedia
            heroMedia={heroMedia ?? undefined}
            legacyMiniDemoId={miniDemoId}
            demoFallbackClassName="h-full w-full"
          />
        </div>
      )}

      {isFeatured && !hasHeroMedia && (
        <div
          className={`${DEMO_SLOT_CLASS} bg-surface-frost-10 transition-colors group-hover:bg-surface-frost-12`}
        />
      )}
    </a>
  );
}
